import {
    AgentSkill,
    AgentSkillHistoryItem,
    AgentSkillListItem,
    AgentSkillToolDefinition,
    AgentSkillInlineTestRequest,
    AgentSkillTestDatasetImportResponse,
    AgentSkillTestRunRequest,
    AgentSkillTestRunResponse,
    SaveAgentSkillRequest,
    SaveAgentSkillTestDatasetRequest,
} from "components/AgentSkills/Models/AgentSkill";
import type { StreamingTokenResult } from "./AcxAgentService";
import { BaseService } from "./BaseService";

export class AgentSkillService extends BaseService {
    protected urlBase = "api/agentskills";

    public getSkills(abortSignal?: AbortSignal) {
        return this.get<AgentSkillListItem[]>(
            new URLSearchParams(),
            "",
            abortSignal,
        );
    }

    public getAvailableTools(abortSignal?: AbortSignal) {
        return this.get<AgentSkillToolDefinition[]>(
            new URLSearchParams(),
            "available-tools",
            abortSignal,
        );
    }

    public getSkill(id: string) {
        return this.get<AgentSkill>(new URLSearchParams(), id);
    }

    public getSkillHistory(id: string) {
        return this.get<AgentSkillHistoryItem[]>(
            new URLSearchParams(),
            `${id}/history`,
        );
    }

    public createSkill(request: SaveAgentSkillRequest) {
        return this.post<AgentSkill>(
            JSON.stringify(request),
            new URLSearchParams(),
            "",
        );
    }

    public updateSkill(id: string, request: SaveAgentSkillRequest) {
        return this.put<AgentSkill>(
            new URLSearchParams(),
            id,
            JSON.stringify(request),
        );
    }

    public importTestDataset(file: File) {
        const formData = new FormData();
        formData.append("datasetFile", file);

        return this.post<AgentSkillTestDatasetImportResponse>(
            formData,
            new URLSearchParams(),
            "import-test-dataset",
        );
    }

    public saveTestDataset(
        id: string,
        request: SaveAgentSkillTestDatasetRequest,
    ) {
        return this.put<AgentSkill>(
            new URLSearchParams(),
            `${id}/test-dataset`,
            JSON.stringify(request),
        );
    }

    public testSkill(request: AgentSkillTestRunRequest) {
        return this.post<AgentSkillTestRunResponse>(
            JSON.stringify(request),
            new URLSearchParams(),
            "test",
        );
    }

    public async *testSkillInline(
        request: AgentSkillInlineTestRequest,
        abortSignal?: AbortSignal,
    ): AsyncIterable<StreamingTokenResult> {
        const response: Response = await this.post(
            JSON.stringify(request),
            new URLSearchParams(),
            "test-inline",
            undefined,
            undefined,
            abortSignal,
            true,
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => "");
            throw new Error(
                `Live skill test failed with ${response.status}: ${errorText}`,
            );
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No live skill test response reader.");

        const decoder = new TextDecoder();
        let buffer = "";
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const parsed = this.extractStreamingTokens(buffer);
                buffer = parsed.remaining;
                for (const token of parsed.tokens) yield token;
            }

            buffer += decoder.decode();
            const parsed = this.extractStreamingTokens(buffer);
            for (const token of parsed.tokens) yield token;
            if (parsed.remaining.trim()) {
                throw new Error("The live skill test returned invalid data.");
            }
        } finally {
            reader.releaseLock();
        }
    }

    private extractStreamingTokens(buffer: string): {
        tokens: StreamingTokenResult[];
        remaining: string;
    } {
        const tokens: StreamingTokenResult[] = [];
        let depth = 0;
        let start = -1;
        let inString = false;
        let escaped = false;
        let consumedThrough = 0;

        for (let index = 0; index < buffer.length; index++) {
            const character = buffer[index];
            if (start === -1) {
                if (character === "{") {
                    start = index;
                    depth = 1;
                    inString = false;
                    escaped = false;
                } else if (!/\s/.test(character)) {
                    consumedThrough = index + 1;
                }
                continue;
            }

            if (escaped) {
                escaped = false;
                continue;
            }
            if (inString && character === "\\") {
                escaped = true;
                continue;
            }
            if (character === '"') {
                inString = !inString;
                continue;
            }
            if (inString) continue;

            if (character === "{") depth++;
            if (character === "}") {
                depth--;
                if (depth === 0) {
                    tokens.push(JSON.parse(buffer.slice(start, index + 1)));
                    consumedThrough = index + 1;
                    start = -1;
                    inString = false;
                    escaped = false;
                }
            }
        }

        return {
            tokens,
            remaining:
                start === -1
                    ? buffer.slice(consumedThrough).trimStart()
                    : buffer.slice(start),
        };
    }

    public deleteSkill(id: string) {
        return this.delete(new URLSearchParams(), id);
    }
}

export default AgentSkillService;
